import React, { useEffect, useRef, useState } from "react";
import { Tooltip } from "@/components/common/Tooltip";

// ---------------------------------------------------------------------------
// StatusIndicator — Generic minimal indicator for transient status feedback
//
// Timer lifecycle:
//   enter(320ms) → hold(holdMs) → collapse(400ms) → fade(400ms) → onDismissed
//
// Pure presentation — no store dependency. Positioning handled by container.
// Shares indicator-* CSS classes with ConnectionIndicator.
// ---------------------------------------------------------------------------

export interface StatusIndicatorProps {
  /** Unique key to reset animation when notification changes */
  id: string;
  /** CSS shape div or any React node */
  icon: React.ReactNode;
  /** Notification message text */
  message: string;
  /** CSS color class: "indicator-color-success" | "indicator-color-error" | etc. */
  colorClass: string;
  /** "transient" auto-dismisses; "persistent" stays until replaced */
  behavior: "transient" | "persistent";
  /** Hold duration before text collapses (default 2500ms) */
  holdMs?: number;
  /** Optional action button (e.g., Dismiss) */
  action?: { label: string; onClick: () => void };
  /** Progress bar 0-100, undefined = no bar */
  progress?: number;
  /** Called after fade-out animation completes */
  onDismissed?: () => void;
  /** ARIA live region politeness (default "polite") */
  ariaLive?: "polite" | "assertive";
}

export const StatusIndicator: React.FC<StatusIndicatorProps> = ({
  id,
  icon,
  message,
  colorClass,
  behavior,
  holdMs = 2500,
  action,
  progress,
  onDismissed,
  ariaLive = "polite",
}) => {
  const [textCollapsed, setTextCollapsed] = useState(false);
  const [fadingOut, setFadingOut] = useState(false);
  const collapseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const fadeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Reset timers and collapse state when id/message changes
  useEffect(() => {
    setTextCollapsed(false);
    setFadingOut(false);

    // Clear previous timers
    if (collapseTimerRef.current) {
      clearTimeout(collapseTimerRef.current);
      collapseTimerRef.current = null;
    }
    if (fadeTimerRef.current) {
      clearTimeout(fadeTimerRef.current);
      fadeTimerRef.current = null;
    }

    if (behavior === "transient") {
      // Collapse text after hold period
      collapseTimerRef.current = setTimeout(() => {
        setTextCollapsed(true);
      }, holdMs);

      // Schedule full fade-out after hold + 1500ms
      fadeTimerRef.current = setTimeout(() => {
        setFadingOut(true);
        // Call onDismissed after fade transition completes (400ms)
        setTimeout(() => {
          onDismissed?.();
        }, 400);
      }, holdMs + 1500);
    }

    return () => {
      if (collapseTimerRef.current) {
        clearTimeout(collapseTimerRef.current);
        collapseTimerRef.current = null;
      }
      if (fadeTimerRef.current) {
        clearTimeout(fadeTimerRef.current);
        fadeTimerRef.current = null;
      }
    };
  }, [id, behavior, holdMs, onDismissed]);

  const hasProgress = progress !== undefined;
  const showProgressBar = hasProgress && progress > 0;

  return (
    <div
      className={`indicator-wrapper${fadingOut ? " indicator-fade-out" : ""}`}
    >
      <div
        className={`indicator ${colorClass}`}
        role="status"
        aria-live={ariaLive}
        aria-label={message}
      >
        <div className="indicator-icon">{icon}</div>
        {message && (
          <div
            className={`indicator-text ${
              behavior === "transient"
                ? `indicator-text-transient${textCollapsed ? " collapsed" : ""}`
                : "indicator-text-persist"
            }`}
          >
            <Tooltip title={message}>
              <span className="indicator-message">{message}</span>
            </Tooltip>
            {action && (
              <>
                <span className="indicator-separator" />
                <button
                  className="indicator-action-btn"
                  onClick={action.onClick}
                  aria-label={action.label}
                >
                  {action.label}
                </button>
              </>
            )}
          </div>
        )}
        {showProgressBar && (
          <div className="indicator-progress">
            <div
              className="indicator-progress-bar"
              style={{
                width: `${Math.min(100, Math.max(0, progress))}%`,
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};
